/**
 * Reviewed browser and audit guidance used by the page-speed specialist
 * calculators and their articles. Keep threshold changes here first, then
 * update the focused regression test with the new reviewed values.
 */
export const PAGE_SPEED_GUIDANCE = {
  scoreBands: {
    excellent: 90,
    good: 75,
    needsImprovement: 50,
  },
  pageLoad: {
    goodSeconds: 2.5,
    poorSeconds: 4,
  },
  coreWebVitals: {
    lcp: { goodSeconds: 2.5, poorSeconds: 4 },
    inp: { goodMs: 200, poorMs: 500 },
    cls: { good: 0.1, poor: 0.25 },
    fieldPercentile: 75,
  },
  mobile: {
    closeToDesktopGapSeconds: 1,
  },
  pwa: {
    auditDimensions: ["Installability", "Performance", "Accessibility", "Best practices"] as const,
  },
  amp: {
    requestBaseline: 20,
    weightPenaltyPer100Kb: 1,
    requestPenaltyPerExtraRequest: 2,
    validationPenaltyPerError: 10,
    validWithAtMostErrors: 0,
  },
} as const;

export type PageSpeedSourceGroup = "coreWebVitals" | "pwa" | "amp";

export const PAGE_SPEED_SOURCES = [
  {
    name: "Google web.dev — Web Vitals",
    url: "https://web.dev/articles/vitals",
    supports: "Good targets: LCP ≤ 2.5 s, INP ≤ 200 ms, and CLS ≤ 0.1.",
  },
  {
    name: "Google web.dev — How Core Web Vitals thresholds were defined",
    url: "https://web.dev/articles/defining-core-web-vitals-thresholds",
    supports: "Good/poor thresholds: LCP ≤ 2.5/> 4 s, INP ≤ 200/> 500 ms, CLS ≤ 0.1/> 0.25, evaluated at the 75th percentile.",
  },
  {
    name: "Chrome for Developers — Lighthouse performance scoring",
    url: "https://developer.chrome.com/docs/lighthouse/performance/performance-scoring",
    supports: "Lighthouse score bands: 0–49 poor, 50–89 needs improvement, and 90–100 good.",
  },
] as const;

export const PAGE_SPEED_ENGINE_FORMULAS = {
  coreWebVitals: `Pass = LCP ≤ ${PAGE_SPEED_GUIDANCE.coreWebVitals.lcp.goodSeconds}s, INP ≤ ${PAGE_SPEED_GUIDANCE.coreWebVitals.inp.goodMs}ms, and CLS ≤ ${PAGE_SPEED_GUIDANCE.coreWebVitals.cls.good.toFixed(2)}`,
} as const;

export const PAGE_SPEED_WIDGET_THRESHOLDS = {
  score: `Calculator planning bands: excellent ≥ ${PAGE_SPEED_GUIDANCE.scoreBands.excellent}; good ≥ ${PAGE_SPEED_GUIDANCE.scoreBands.good}; needs improvement ≥ ${PAGE_SPEED_GUIDANCE.scoreBands.needsImprovement}`,
  pageLoad: `Calculator planning target: good ≤ ${PAGE_SPEED_GUIDANCE.pageLoad.goodSeconds}s; investigate above ${PAGE_SPEED_GUIDANCE.pageLoad.poorSeconds}s (not a universal cutoff).`,
  coreWebVitals: PAGE_SPEED_ENGINE_FORMULAS.coreWebVitals,
  lcp: `Good LCP ≤ ${PAGE_SPEED_GUIDANCE.coreWebVitals.lcp.goodSeconds}s; poor above ${PAGE_SPEED_GUIDANCE.coreWebVitals.lcp.poorSeconds}s.`,
  cls: `Good CLS ≤ ${PAGE_SPEED_GUIDANCE.coreWebVitals.cls.good.toFixed(2)}; poor above ${PAGE_SPEED_GUIDANCE.coreWebVitals.cls.poor}.`,
  mobile: `Calculator comparison: close to desktop means a gap of ≤ ${PAGE_SPEED_GUIDANCE.mobile.closeToDesktopGapSeconds}s.`,
  pwa: `Calculator planning bands: excellent ≥ ${PAGE_SPEED_GUIDANCE.scoreBands.excellent}; review below ${PAGE_SPEED_GUIDANCE.scoreBands.good}.`,
  amp: `Calculator planning rule: valid AMP requires no more than ${PAGE_SPEED_GUIDANCE.amp.validWithAtMostErrors} validation errors.`,
} as const;

export const PAGE_SPEED_ARTICLE_BENCHMARKS = {
  seoScore: `This calculator treats an audit score of ${PAGE_SPEED_GUIDANCE.scoreBands.excellent} or more as broad planning coverage, ${PAGE_SPEED_GUIDANCE.scoreBands.good}–${PAGE_SPEED_GUIDANCE.scoreBands.excellent - 1} as a working baseline, and a lower score as an agenda rather than a ranking forecast. These are calculator heuristics, not a universal SEO score.`,
  pageLoad: `This calculator uses a result at or below roughly ${PAGE_SPEED_GUIDANCE.pageLoad.goodSeconds} seconds as a performance planning target, while a result above ${PAGE_SPEED_GUIDANCE.pageLoad.poorSeconds} seconds deserves investigation across mobile devices and slower networks. This is not a universal Google cutoff.`,
  coreWebVitals: `The good thresholds are LCP ≤ ${PAGE_SPEED_GUIDANCE.coreWebVitals.lcp.goodSeconds} s, INP ≤ ${PAGE_SPEED_GUIDANCE.coreWebVitals.inp.goodMs} ms, and CLS ≤ ${PAGE_SPEED_GUIDANCE.coreWebVitals.cls.good.toFixed(2)}. Field data should normally be evaluated at the ${PAGE_SPEED_GUIDANCE.coreWebVitals.fieldPercentile}th percentile for the relevant user population.`,
  lcp: `LCP is considered good at or below ${PAGE_SPEED_GUIDANCE.coreWebVitals.lcp.goodSeconds} seconds, needs improvement above ${PAGE_SPEED_GUIDANCE.coreWebVitals.lcp.goodSeconds} up to ${PAGE_SPEED_GUIDANCE.coreWebVitals.lcp.poorSeconds} seconds, and poor above ${PAGE_SPEED_GUIDANCE.coreWebVitals.lcp.poorSeconds} seconds. The largest element can differ between templates and viewport sizes.`,
  cls: `CLS ≤ ${PAGE_SPEED_GUIDANCE.coreWebVitals.cls.good.toFixed(2)} is good, above ${PAGE_SPEED_GUIDANCE.coreWebVitals.cls.good.toFixed(2)} up to ${PAGE_SPEED_GUIDANCE.coreWebVitals.cls.poor} needs improvement, and above ${PAGE_SPEED_GUIDANCE.coreWebVitals.cls.poor} is poor. Review the individual shift sources because a site-wide average can hide a severe template.`,
  pwaScore: `Scores near ${PAGE_SPEED_GUIDANCE.scoreBands.excellent} or above suggest broad audit coverage, while a score below ${PAGE_SPEED_GUIDANCE.scoreBands.good} merits a structured review. The browser's install prompt also depends on concrete manifest, service-worker, and security requirements.`,
  ampPage: `A score of ${PAGE_SPEED_GUIDANCE.scoreBands.excellent} or more indicates a compact, clean starting point for review. Zero validation errors matters more than a small size improvement because invalid markup can prevent the intended AMP experience.`,
} as const;

export function getPageSpeedSourceGroups(specialist: string): readonly PageSpeedSourceGroup[] {
  const key = specialist.replace(/ calculator$/i, "").replace(/[-_]+/g, " ").trim().toLowerCase();
  const sources = PAGE_SPEED_PROVENANCE.specialistSources as Record<string, readonly PageSpeedSourceGroup[]>;
  return sources[key] ?? ["coreWebVitals"];
}

/**
 * Official references for the assumptions used by the page-speed specialists.
 * Keep the reviewed date with the links so a threshold update has an explicit
 * maintenance signal in both the calculator and its article.
 */
export const PAGE_SPEED_PROVENANCE = {
  reviewedDate: "1 September 2026",
  sourceGroups: {
    coreWebVitals: {
      label: "Core Web Vitals",
      links: [
        { label: "Web Vitals", href: "https://web.dev/articles/vitals" },
        { label: "Thresholds", href: "https://web.dev/articles/defining-core-web-vitals-thresholds" },
      ],
    },
    pwa: {
      label: "PWA audit requirements",
      links: [
        { label: "Lighthouse PWA audits", href: "https://developer.chrome.com/docs/lighthouse/pwa/" },
        { label: "PWA guidance", href: "https://web.dev/learn/pwa/" },
      ],
    },
    amp: {
      label: "AMP validation guidance",
      links: [
        { label: "Validate AMP pages", href: "https://amp.dev/documentation/guides-and-tutorials/learn/validation-workflow/validate_amp" },
        { label: "AMP validator", href: "https://validator.amp.dev/" },
      ],
    },
  },
  specialistSources: {
    "seo score": ["coreWebVitals"],
    "page load": ["coreWebVitals"],
    "core web vitals": ["coreWebVitals"],
    lcp: ["coreWebVitals"],
    cls: ["coreWebVitals"],
    "mobile performance": ["coreWebVitals"],
    "pwa score": ["pwa"],
    "amp page": ["amp"],
  } satisfies Record<string, readonly PageSpeedSourceGroup[]>,
} as const;
